/**
 * My Leave Balance page - Shows current user's leave balances only
 */

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { leaveApi } from '../api/leaveApi'
import LoadingPage from '@/shared/components/LoadingPage'
import { useAuth } from '@/core/auth/AuthContext'

const LeaveBalancePage = () => {
  const { user } = useAuth()
  const currentYear = new Date().getFullYear()

  const { data, isLoading, error } = useQuery({
    queryKey: ['leaveBalances'],
    queryFn: () => leaveApi.getLeaveBalances(),
  })

  if (isLoading) return <LoadingPage />

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-red-100 p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to load leave balances</h3>
            <p className="text-gray-500">Please try refreshing the page</p>
          </div>
        </div>
      </div>
    )
  }

  const balances = data?.data || []

  // Calculate totals (exclude unlimited leave types from allocated/available counts)
  const limitedBalances = balances.filter((b) => b.total_allocated > 0)
  const totalAllocated = limitedBalances.reduce((sum, b) => sum + b.total_allocated, 0)
  const totalUsed = balances.reduce((sum, b) => sum + b.used, 0)
  const totalPending = balances.reduce((sum, b) => sum + b.pending, 0)
  const totalAvailable = limitedBalances.reduce((sum, b) => sum + (b.available ?? 0), 0)

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-rose-700 px-6 py-8 shadow-lg">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 text-red-200 text-sm mb-2">
            <Link to="/leave" className="hover:text-white transition-colors">Leave</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white">My Balance</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-white font-bold text-xl shadow-lg">
                {user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'ME'}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white mb-1">My Leave Balance</h1>
                <p className="text-red-200">{user?.full_name} • {currentYear}</p>
              </div>
            </div>
            <Link
              to="/leave/new"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-red-700 font-semibold rounded-xl hover:bg-red-50 transition-all shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              New Request
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 -mt-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-500">Total Allocated</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{totalAllocated}</p>
            <p className="text-sm text-gray-400">days this year</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-500">Used</span>
            </div>
            <p className="text-3xl font-bold text-red-600">{totalUsed}</p>
            <p className="text-sm text-gray-400">days taken</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-500">Pending</span>
            </div>
            <p className="text-3xl font-bold text-amber-600">{totalPending}</p>
            <p className="text-sm text-gray-400">awaiting approval</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-500">Available</span>
            </div>
            <p className="text-3xl font-bold text-emerald-600">{totalAvailable}</p>
            <p className="text-sm text-gray-400">days remaining</p>
          </div>
        </div>

        {/* Leave Balance Cards */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Balance by Leave Type
          </h2>

          {balances.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Leave Balances Yet</h3>
              <p className="text-gray-500 mb-6">Your leave balances will appear here once they are allocated.</p>
              <p className="text-sm text-gray-400">Contact HR if you believe this is an error.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {balances.map((balance) => {
                const color = balance.leave_type?.color || '#6b7280'
                const isUnlimited = balance.total_allocated === 0
                const usagePercent = !isUnlimited && balance.total_allocated > 0
                  ? Math.round((balance.used / balance.total_allocated) * 100)
                  : 0

                return (
                  <div
                    key={balance.id}
                    className="bg-white rounded-2xl shadow-sm border-2 p-6 hover:shadow-md transition-all"
                    style={{ borderColor: `${color}30` }}
                  >
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-5">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
                        style={{ backgroundColor: color }}
                      >
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">{balance.leave_type?.name}</h3>
                        <p className="text-sm text-gray-500">{currentYear} Balance</p>
                      </div>
                    </div>

                    {/* Available (main highlight) — hidden for unlimited leave */}
                    {!isUnlimited && (
                      <div
                        className="rounded-xl p-4 mb-4"
                        style={{ backgroundColor: `${color}10` }}
                      >
                        <div className="flex items-baseline justify-between">
                          <span className="text-sm font-medium text-gray-600">Available</span>
                          <div className="text-right">
                            <span className="text-3xl font-bold" style={{ color }}>{balance.available}</span>
                            <span className="text-sm text-gray-500 ml-1">days</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Progress bar — hidden for unlimited leave */}
                    {!isUnlimited && (
                      <div className="mb-4">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>Usage</span>
                          <span>{usagePercent}%</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${usagePercent}%`, backgroundColor: color }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Stats grid */}
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-2 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Allocated</p>
                        <p className="font-semibold text-gray-900">{isUnlimited ? '-' : balance.total_allocated}</p>
                      </div>
                      <div className="p-2 bg-red-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Used</p>
                        <p className="font-semibold text-red-600">{balance.used}</p>
                      </div>
                      <div className="p-2 bg-amber-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Pending</p>
                        <p className="font-semibold text-amber-600">{balance.pending}</p>
                      </div>
                    </div>

                    {/* Carried forward if any */}
                    {balance.carried_forward > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Carried Forward
                          </span>
                          <span className="font-semibold text-red-600">+{balance.carried_forward} days</span>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
          <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/leave/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 font-medium rounded-xl hover:bg-red-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Request Leave
            </Link>
            <Link
              to="/leave/requests"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              View My Requests
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LeaveBalancePage
